import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Organization, OrgHat, PolicyApprovalWorkflow, PolicyApprovalStep, PolicyApprover } from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Input data for the approvers selector dialog
 */
export interface PolicyApproversSelectorDialogInput {
    organization: Organization;
    currentWorkflow?: PolicyApprovalWorkflow;
    mode: 'view' | 'edit';
}

/**
 * Result returned from the dialog
 */
export interface PolicyApproversSelectorDialogResult {
    workflow: PolicyApprovalWorkflow;
}

/**
 * User option for selection dropdown
 */
interface UserOption {
    uid: string;
    name: string;
    email?: string;
    hatKey?: string;
    hatName?: string;
    type: 'user' | 'hat';
}

@Component({
    selector: 'shared-org-policy-approvers-selector',
    imports: [MaterialModule, CommonModule, FormsModule, DragDropModule],
    templateUrl: './org-policy-approvers-selector.html',
    styleUrls: ['./org-policy-approvers-selector.scss', '../org-dialog.scss'],
})
export class OrgPolicyApproversSelectorComponent implements OnInit {
    // === Approval Steps ===
    public approvalSteps: PolicyApprovalStep[] = [];

    // === User Selection ===
    public availableUsers: UserOption[] = [];
    public filteredUsers: UserOption[] = [];
    public searchTerm = '';

    // === Approver Search (for dropdown search) ===
    public approverSearchTerm = '';
    public filteredApprovers: UserOption[] = [];

    constructor(
        public dialogRef: MatDialogRef<OrgPolicyApproversSelectorComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PolicyApproversSelectorDialogInput
    ) {}

    ngOnInit(): void {
        this.loadAvailableUsers();
        this.loadCurrentWorkflow();
    }

    // === Load Data ===

    /**
     * Build list of available users from org hats
     */
    private loadAvailableUsers(): void {
        const org = this.data.organization;
        const orgHats = org?.orgHats;
        const userMap = new Map<string, UserOption>();

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat) {
                    const hatName = hat.name || hatKey;
                    const userKeys = hat.assignedToArray || hat.userKeys || [];

                    for (const uid of userKeys) {
                        // Get user name from orgUsers if available
                        const orgUser = org?.orgUsers?.[uid];
                        const userName = orgUser?.name || orgUser?.userName || `User ${uid.substring(0, 6)}`;
                        const email = orgUser?.email;

                        // Create a hat-based option
                        const optionKey = `${uid}_${hatKey}`;
                        if (!userMap.has(optionKey)) {
                            userMap.set(optionKey, {
                                uid,
                                name: userName,
                                email,
                                hatKey,
                                hatName,
                                type: 'hat',
                            });
                        }

                        // Also add a direct user option if not already present
                        if (!userMap.has(uid)) {
                            userMap.set(uid, {
                                uid,
                                name: userName,
                                email,
                                type: 'user',
                            });
                        }
                    }
                }
            }
        }

        this.availableUsers = Array.from(userMap.values());
        this.filteredUsers = [...this.availableUsers];
        this.filteredApprovers = [...this.availableUsers];
    }

    /**
     * Load existing workflow if provided
     */
    private loadCurrentWorkflow(): void {
        if (this.data.currentWorkflow?.steps) {
            this.approvalSteps = [...this.data.currentWorkflow.steps];
        }
    }

    // === Search ===

    public filterUsers(): void {
        if (!this.searchTerm.trim()) {
            this.filteredUsers = [...this.availableUsers];
            return;
        }

        const term = this.searchTerm.toLowerCase();
        this.filteredUsers = this.availableUsers.filter(
            (user) =>
                user.name.toLowerCase().includes(term) ||
                user.hatName?.toLowerCase().includes(term) ||
                user.email?.toLowerCase().includes(term)
        );
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.filteredUsers = [...this.availableUsers];
    }

    // === Approver Dropdown Search ===

    /**
     * Filter approvers in the dropdown search
     */
    public filterApprovers(): void {
        if (!this.approverSearchTerm.trim()) {
            this.filteredApprovers = [...this.availableUsers];
            return;
        }

        const term = this.approverSearchTerm.toLowerCase();
        this.filteredApprovers = this.availableUsers.filter(
            (user) =>
                user.name.toLowerCase().includes(term) ||
                user.hatName?.toLowerCase().includes(term) ||
                user.email?.toLowerCase().includes(term)
        );
    }

    /**
     * Clear approver search in dropdown
     */
    public clearApproverSearch(): void {
        this.approverSearchTerm = '';
        this.filteredApprovers = [...this.availableUsers];
    }

    /**
     * Get the UserOption that matches the current step's approver
     */
    public getSelectedUserOption(step: PolicyApprovalStep): UserOption | null {
        if (this.isStepIncomplete(step)) {
            return null;
        }

        // Find matching user option
        const approver = step.approver;
        if (approver.type === 'hat' && approver.hatKey) {
            return this.availableUsers.find((u) => u.type === 'hat' && u.hatKey === approver.hatKey) || null;
        } else if (approver.type === 'user' && approver.uid) {
            return this.availableUsers.find((u) => u.type === 'user' && u.uid === approver.uid && !u.hatKey) || null;
        }
        return null;
    }

    // === Step Management ===

    /**
     * Add a new step immediately (like templates do)
     * Creates a step with placeholder values that user can edit inline
     */
    public addNewStep(): void {
        const stepNumber = this.approvalSteps.length + 1;
        const newStep: PolicyApprovalStep = {
            order: this.approvalSteps.length,
            name: `Step ${stepNumber}`,
            approver: this.getPlaceholderApprover('Select approver'),
            status: 'pending',
        };

        this.approvalSteps.push(newStep);
        this.updateStepOrders();
    }

    /**
     * Remove a step
     */
    public removeStep(index: number): void {
        this.approvalSteps.splice(index, 1);
        this.updateStepOrders();
    }

    /**
     * Handle drag and drop reordering
     */
    public dropStep(event: CdkDragDrop<PolicyApprovalStep[]>): void {
        moveItemInArray(this.approvalSteps, event.previousIndex, event.currentIndex);
        this.updateStepOrders();
    }

    /**
     * Update step order numbers after reorder
     */
    private updateStepOrders(): void {
        this.approvalSteps.forEach((step, index) => {
            step.order = index;
        });
    }

    /**
     * Handle step name blur - ensure name isn't empty
     */
    public onStepNameBlur(step: PolicyApprovalStep): void {
        if (!step.name || step.name.trim() === '') {
            step.name = `Step ${step.order + 1}`;
        }
    }

    // === Preset Templates ===

    /**
     * Apply a preset approval template
     */
    public applyTemplate(template: 'simple' | 'standard' | 'executive'): void {
        switch (template) {
            case 'simple':
                // Single manager approval
                this.approvalSteps = [
                    {
                        order: 0,
                        name: 'Manager Review',
                        approver: this.getPlaceholderApprover('Select manager'),
                        status: 'pending',
                    },
                ];
                break;

            case 'standard':
                // Manager -> HR
                this.approvalSteps = [
                    {
                        order: 0,
                        name: 'Department Manager',
                        approver: this.getPlaceholderApprover('Select manager'),
                        status: 'pending',
                    },
                    {
                        order: 1,
                        name: 'HR Review',
                        approver: this.getPlaceholderApprover('Select HR representative'),
                        status: 'pending',
                    },
                ];
                break;

            case 'executive':
                // Manager -> HR -> Executive
                this.approvalSteps = [
                    {
                        order: 0,
                        name: 'Department Manager',
                        approver: this.getPlaceholderApprover('Select manager'),
                        status: 'pending',
                    },
                    {
                        order: 1,
                        name: 'HR Director',
                        approver: this.getPlaceholderApprover('Select HR director'),
                        status: 'pending',
                    },
                    {
                        order: 2,
                        name: 'Executive Approval',
                        approver: this.getPlaceholderApprover('Select executive'),
                        status: 'pending',
                    },
                ];
                break;
        }
    }

    private getPlaceholderApprover(placeholder: string): PolicyApprover {
        return {
            type: 'user',
            uid: '',
            name: placeholder,
        };
    }

    /**
     * Check if a step needs an approver selected
     */
    public isStepIncomplete(step: PolicyApprovalStep): boolean {
        // Check if approver is selected (either has uid for user or hatKey for hat)
        return step.approver.type === 'user' ? !step.approver.uid : !step.approver.hatKey;
    }

    /**
     * Update approver for a step
     */
    public updateStepApprover(step: PolicyApprovalStep, user: UserOption): void {
        if (user.type === 'hat') {
            step.approver = {
                type: 'hat',
                hatKey: user.hatKey,
                name: user.name,
                email: user.email,
            };
        } else {
            step.approver = {
                type: 'user',
                uid: user.uid,
                name: user.name,
                email: user.email,
            };
        }
    }

    // === Status Helpers ===

    public getStepStatusIcon(status: string): string {
        switch (status) {
            case 'approved':
                return 'check_circle';
            case 'rejected':
                return 'cancel';
            case 'pending':
                return 'hourglass_empty';
            case 'waiting':
                return 'schedule';
            case 'skipped':
                return 'skip_next';
            default:
                return 'help';
        }
    }

    public getStepStatusColor(status: string): string {
        switch (status) {
            case 'approved':
                return 'primary';
            case 'rejected':
                return 'warn';
            case 'pending':
                return 'accent';
            case 'waiting':
                return '';
            case 'skipped':
                return '';
            default:
                return '';
        }
    }

    // === Validation ===

    public get isValid(): boolean {
        if (this.approvalSteps.length === 0) return true; // No approval required
        return this.approvalSteps.every((step) => !this.isStepIncomplete(step));
    }

    public get hasIncompleteSteps(): boolean {
        return this.approvalSteps.some((step) => this.isStepIncomplete(step));
    }

    // === Dialog Actions ===

    public confirm(): void {
        const workflow: PolicyApprovalWorkflow = {
            steps: this.approvalSteps,
            currentStepIndex: -1,
            workflowStatus: 'not_started',
            canResubmit: true,
        };

        const result: PolicyApproversSelectorDialogResult = {
            workflow,
        };

        this.dialogRef.close(result);
    }

    public cancel(): void {
        this.dialogRef.close(null);
    }

    /**
     * Clear all approval steps
     */
    public clearAllSteps(): void {
        this.approvalSteps = [];
    }
}
